import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import moment from 'moment';
import ConfigProvider from 'antd/lib/config-provider';
import Radio from 'antd/lib/radio';
import Empty from 'antd/lib/empty';
import Table from 'antd/lib/table';
import Switch from 'antd/lib/switch';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import notification from 'antd/lib/notification';
import Tooltip from 'antd/lib/tooltip';
import { getEventsList, snoozeAlert } from '../../../../../../API';
import { CustomSnoozeModal } from '../../../../../CustomSnoozeModal';
import { config } from '../../../../../../config';

// ICONS
import snoozeIcon from '../../../../../../icons/snooze-blue.svg';
import snoozeDropdownArrow from '../../../../../../icons/arrow-down-blue.svg';
import downloadIcon from '../../../../../../icons/download.svg';
import { ReactComponent as InfoIcon } from '../../../../../BuildingStatusWidget/assets/Infobox_info_icon.svg';
import { CheckmarkIcon } from '../../../CheckmarkIcon';

// IMAGES
import noAlerts from '../../../../../../images/no-alerts.png';
import noSnoozedAlertsLight from '../../../../../../images/no-snoozed-alerts-light.png';
import noSnoozedAlertsDark from '../../../../../../images/no-snoozed-alerts-dark.png';

import './ActiveAlertsTable.less';

const { REACT_APP_BUILD_ENV } = config;

export const ActiveAlertsTable = (props) => {
  const { activeEvents, updateActiveEvents, onDownloadClick, theme } = props;
  const { building } = useParams();
  const history = useHistory();
  const [nonSnoozedActiveEvents, setNonSnoozedActiveEvents] = useState([]);
  const [snoozedActiveEvents, setSnoozedActiveEvents] = useState([]);
  const [activeAlertsPrioritySorting, setActiveAlertsPrioritySorting] = useState(false);
  const [activeAlertsCategory, setActiveAlertsCategory] = useState('New');
  const [snoozedAlert, setSnoozedAlert] = useState({});
  const [isCustomSnoozeModalOpen, setIsCustomSnoozeModalOpen] = useState(false);
  const [isDownloadInfoOpen, setDownloadInfoOpen] = useState(false);

  useEffect(() => {
    const sortedActiveEvents = activeEvents.sort((a, b) => {
      if ((a.severity === 'low' || a.severity === 'medium') && b.severity === 'high') return 1;
      if (a.severity === 'low' && b.severity === 'medium') return 1;
      if ((a.severity === 'high' || a.severity === 'medium') && b.severity === 'low') return -1;
      if (a.severity === 'high' && b.severity === 'medium') return -1;
      return 0;
    });

    const sortedNonSnoozedActiveEvents = sortedActiveEvents.filter(
      (event) => event.snooze_context.is_snoozed === false,
    );
    const sortedSnoozedActiveEvents = sortedActiveEvents.filter((event) => event.snooze_context.is_snoozed === true);

    updateActiveEvents(sortedActiveEvents);
    setNonSnoozedActiveEvents(sortedNonSnoozedActiveEvents);
    setSnoozedActiveEvents(sortedSnoozedActiveEvents);
    setActiveAlertsPrioritySorting(true);
  }, [building]);

  const sortActiveAlerts = () => {
    let sortedNonSnoozedActiveEvents = [];
    let sortedSnoozedActiveEvents = [];

    if (activeAlertsPrioritySorting) {
      sortedNonSnoozedActiveEvents = nonSnoozedActiveEvents.sort(
        (a, b) => moment(b.last_seen_timestamp) - moment(a.last_seen_timestamp),
      );
      sortedSnoozedActiveEvents = snoozedActiveEvents.sort(
        (a, b) => moment(b.last_seen_timestamp) - moment(a.last_seen_timestamp),
      );
    } else {
      sortedNonSnoozedActiveEvents = nonSnoozedActiveEvents.sort((a, b) => {
        if ((a.severity === 'low' || a.severity === 'medium') && b.severity === 'high') return 1;
        if (a.severity === 'low' && b.severity === 'medium') return 1;
        if ((a.severity === 'high' || a.severity === 'medium') && b.severity === 'low') return -1;
        if (a.severity === 'high' && b.severity === 'medium') return -1;
        return 0;
      });
      sortedSnoozedActiveEvents = snoozedActiveEvents.sort((a, b) => {
        if ((a.severity === 'low' || a.severity === 'medium') && b.severity === 'high') return 1;
        if (a.severity === 'low' && b.severity === 'medium') return 1;
        if ((a.severity === 'high' || a.severity === 'medium') && b.severity === 'low') return -1;
        if (a.severity === 'high' && b.severity === 'medium') return -1;
        return 0;
      });
    }

    setNonSnoozedActiveEvents(sortedNonSnoozedActiveEvents);
    setSnoozedActiveEvents(sortedSnoozedActiveEvents);
    setActiveAlertsPrioritySorting(!activeAlertsPrioritySorting);
  };

  const onSnoozeButtonPress = async (alert) => {
    const { alert_uid_hash } = alert;
    let response;

    if (alert.snooze_context.is_snoozed) {
      response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, 0);

      if (response === true) {
        // Handle success
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert has been unsnoozed`,
          description: `You will resume receiving notifications.`,
        });
      } else {
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert could not be unsnoozed`,
          description: `We encountered an error while trying to unsnooze this alert. Please try again.`,
        });
      }
    } else {
      response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, 3);

      if (response === true) {
        // Handle success
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert has been snoozed`,
          description: `You will resume receiving notifications when you unsnooze it.`,
        });
      } else {
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert could not be snoozed`,
          description: `We encountered an error while trying to snooze this alert. Please try again.`,
        });
      }
    }

    const activeEventsFromApi = await getEventsList(REACT_APP_BUILD_ENV, building, { is_resolved: false });
    const nonSnoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === false,
    );
    const snoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === true,
    );
    setNonSnoozedActiveEvents(nonSnoozedActiveEventsFromApi);
    setSnoozedActiveEvents(snoozedActiveEventsFromApi);
    updateActiveEvents(activeEventsFromApi.results);
  };

  const submitSnoozeAlert = async (hours) => {
    const { alert_uid_hash } = snoozedAlert;

    setIsCustomSnoozeModalOpen(false);
    const response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, hours);

    if (response === true) {
      // Handle success
      notification.info({
        className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
        placement: 'topRight',
        duration: 5,
        icon: <CheckmarkIcon theme={theme} />,
        message: `This alert has been snoozed`,
        description: `You will resume receiving notifications from it starting ${moment()
          .add(hours, 'hours')
          .format('LLL')}`,
      });
    } else {
      notification.info({
        className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
        placement: 'topRight',
        duration: 5,
        icon: <CheckmarkIcon theme={theme} />,
        message: `This alert could not be snoozed`,
        description: `We encountered an error while trying to snooze this alert. Please try again.`,
      });
    }

    const activeEventsFromApi = await getEventsList(REACT_APP_BUILD_ENV, building, { is_resolved: false });
    const nonSnoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === false,
    );
    const snoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === true,
    );
    setNonSnoozedActiveEvents(nonSnoozedActiveEventsFromApi);
    setSnoozedActiveEvents(snoozedActiveEventsFromApi);
    updateActiveEvents(activeEventsFromApi.results);
  };

  const getSeverityColour = (alert) => {
    if (alert.severity === 'high') return '#EA856F';
    if (alert.severity === 'medium') return '#DEC802';
    return '#796FEA';
  };

  const renderNoActiveAlerts = () => {
    return (
      <div className="alerts-overview-no-active-alerts">
        <div className="alerts-overview-no-active-alerts-icon">
          <CheckmarkIcon theme="green" />
        </div>
        <div className="alerts-overview-no-active-alerts-content">
          <div className="alerts-overview-no-active-alerts-title">
            <strong>Good news!</strong>
            <span>Nothing needs your attention right now.</span>
          </div>
          <span>We will alert you when there are problems happening with your building!</span>
        </div>
      </div>
    );
  };

  const renderNoNonSnoozedAlerts = () => {
    return (
      <Empty
        image={noAlerts}
        description={
          <div className="alerts-overview-no-snoozed-alerts-content">
            <strong>Good News! Nothing needs your attention right now</strong>
            <span>We will alert you when there are problems happening with your building</span>
          </div>
        }
      />
    );
  };

  const renderNoSnoozedAlerts = () => {
    return (
      <Empty
        image={theme === 'light' ? noSnoozedAlertsLight : noSnoozedAlertsDark}
        description={
          <div className="alerts-overview-no-snoozed-alerts-content">
            <strong>Alerts that have been snoozed will appear here</strong>
            <span>You can snooze your alerts when you want to temporarily pause e-mail and text notifications</span>
          </div>
        }
      />
    );
  };

  const snoozeItems = [
    {
      key: 'Snooze',
      disabled: true,
      label: <div style={{ color: '#ced7e0' }}>Snooze for...</div>,
    },
    {
      key: '3H',
      label: (
        <div
          onClick={() => {
            submitSnoozeAlert(3);
          }}
        >
          3 hours
        </div>
      ),
    },
    {
      key: '6H',
      label: (
        <div
          onClick={() => {
            submitSnoozeAlert(6);
          }}
        >
          6 hours
        </div>
      ),
    },
    {
      key: '12H',
      label: (
        <div
          onClick={() => {
            submitSnoozeAlert(12);
          }}
        >
          12 hours
        </div>
      ),
    },
    {
      key: '24H',
      label: (
        <div
          onClick={() => {
            submitSnoozeAlert(24);
          }}
        >
          24 hours
        </div>
      ),
    },
    {
      key: 'CH',
      label: (
        <div
          onClick={() => {
            setIsCustomSnoozeModalOpen(true);
          }}
        >
          Custom
        </div>
      ),
    },
  ];

  const activeAlertsTableColumns = [
    {
      title: 'ALERTS',
      dataIndex: ['name', 'severity'],
      className: 'alerts-overview-table-column-type',
      render: (text, alert) => {
        const incidentId = alert.incident_uid_hash;
        const eventId = alert.uid_hash;
        const severity = alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1);
        return (
          <div className="alerts-overview-table-column-type-container">
            <div
              className="alerts-overview-table-column-type-colour"
              style={{ borderLeftColor: getSeverityColour(alert) }}
            />
            <div className="alerts-overview-table-column-type-contents">
              {activeAlertsCategory === 'Snoozed' && (
                <span className="alerts-overview-item-snoozed-until">
                  {`Snoozed until ${moment(alert.snooze_context.snoozed_until).format('MMM D, YYYY (LT)')}`}
                </span>
              )}
              <span
                className="alerts-overview-item-title"
                onClick={() => history.push(`/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}`)}
                onKeyDown={() => history.push(`/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}`)}
                role="link"
                tabIndex={0}
              >
                {alert.name}
              </span>
              <span className="alerts-overview-item-priority">{`Alert Tier: ${severity}`}</span>
              <span className="alerts-overview-item-info mobile">
                {`${moment(alert.last_seen_timestamp).fromNow()} • ${moment(alert.last_seen_timestamp).format(
                  'MMM D, YYYY (LT)',
                )}`}
              </span>
              <div className="alerts-overview-item-buttons">
                <Button
                  className="alerts-overview-view-issue-button"
                  onClick={() => history.push(`/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}`)}
                >
                  View Alert
                </Button>
                <div className="alerts-overview-item-snooze-container">
                  {alert.snooze_context.is_snoozed ? (
                    <Button
                      className={`snooze-component snooze-component-snoozed snooze-component-${theme}`}
                      onClick={() => onSnoozeButtonPress(alert)}
                    >
                      Unsnooze
                    </Button>
                  ) : (
                    <>
                      <Button
                        className={`snooze-component snooze-component-unsnoozed snooze-component-${theme}`}
                        onClick={() => onSnoozeButtonPress(alert)}
                      >
                        <img src={snoozeIcon} alt="snooze" />
                      </Button>
                      <Dropdown
                        menu={{ items: snoozeItems }}
                        trigger={['click']}
                        onClick={() => setSnoozedAlert(alert)}
                      >
                        <Button className={`dropdown-component dropdown-component-${theme}`}>
                          <img src={snoozeDropdownArrow} alt="dropdown" />
                        </Button>
                      </Dropdown>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'IDENTIFIED',
      dataIndex: 'last_seen_timestamp',
      className: 'desktop',
      render: (last_seen_timestamp) => {
        return (
          <span className="alerts-overview-item-info">
            {`${moment(last_seen_timestamp).fromNow()} • ${moment(last_seen_timestamp).format('MMM D, YYYY (LT)')}`}
          </span>
        );
      },
    },
    {
      title: 'SYSTEM',
      dataIndex: 'hvac_system_display',
      className: 'desktop',
      render: (system) => {
        return <span className="alerts-overview-item-info">{`${system}`}</span>;
      },
    },
    {
      title: 'SEEN',
      dataIndex: 'seen_by',
      className: 'desktop',
      render: (users) => {
        // eslint-disable-next-line react/destructuring-assignment
        if (users.length > 0) {
          return (
            <Avatar.Group className="alerts-overview-item-users-container">
              {users.map((user) => {
                const colour = user.avatar_color || '#ffffff';
                const firstAndLastNames = user.name.split(' ');
                return (
                  <Tooltip
                    key={firstAndLastNames}
                    overlayClassName={`app-tooltip-${theme}`}
                    placement="bottom"
                    title={user.name}
                  >
                    <Avatar className="app-profile-icon" alt="profile icon" style={{ backgroundColor: colour }}>
                      {`${firstAndLastNames[0][0]}${firstAndLastNames[1][0]}`}
                    </Avatar>
                  </Tooltip>
                );
              })}
            </Avatar.Group>
          );
        }
        return <span className="alerts-overview-item-info">Nobody yet</span>;
      },
    },
  ];

  const hasActiveAlerts = activeEvents.length > 0;

  return (
    <div className="alerts-overview-section">
      <div className="alerts-overview-card-header">
        <h1 className="alerts-overview-card-title">{`Current Alerts (${activeEvents.length})`}</h1>
        <div className="alerts-overview-card-header-actions">
          {hasActiveAlerts && (
            <div className="alerts-overview-toggle-sort mobile">
              <span>Sort by tier</span>
              <Switch defaultChecked onChange={sortActiveAlerts} />
            </div>
          )}
          <div className="alerts-overview-download-wrapper">
            <div className="alerts-overview-download-button" role="presentation" onClick={onDownloadClick}>
              Download Data
              <img className="download-img" src={downloadIcon} alt="download-icon" />
            </div>
            <div
              onClick={() => setDownloadInfoOpen((prev) => !prev)}
              className="alerts-overview-download-info-box"
              role="presentation"
              data-testid="download-info-toggle"
            >
              <InfoIcon color={theme === 'light' ? '#4b4b4b55' : '#14bdc9'} />
            </div>
            {isDownloadInfoOpen && (
              <div className="alerts-overview-download-info">
                <div>
                  <p className="alerts-overview-download-info-title">Timezone</p>
                  <br />
                  Event start and end times in the downloaded CSV are in UTC, not your local time zone.
                </div>
                <div
                  onClick={() => setDownloadInfoOpen(() => false)}
                  className="alerts-overview-download-info-cta"
                  role="presentation"
                  data-testid="download-info-close"
                >
                  OK
                </div>
                <div className="alerts-overview-download-info-arrow" />
              </div>
            )}
          </div>
        </div>
      </div>
      {hasActiveAlerts ? (
        <>
          <div className="alerts-overview-toggle-container">
            <div className="alerts-overview-toggle-category">
              <Radio.Group onChange={(e) => setActiveAlertsCategory(e.target.value)} defaultValue="New">
                <Radio.Button className="alerts-overview-active-new-button" value="New">
                  {`New (${nonSnoozedActiveEvents.length})`}
                </Radio.Button>
                <Radio.Button className="alerts-overview-active-snoozed-button" value="Snoozed">
                  {`Snoozed (${snoozedActiveEvents.length})`}
                </Radio.Button>
              </Radio.Group>
            </div>
            <div className="alerts-overview-toggle-sort desktop">
              <span>Sort by tier</span>
              <Switch defaultChecked onChange={sortActiveAlerts} />
            </div>
          </div>
          <ConfigProvider
            renderEmpty={activeAlertsCategory === 'New' ? renderNoNonSnoozedAlerts : renderNoSnoozedAlerts}
          >
            <Table
              className={`alerts-overview-table alerts-overview-table-active aat-${theme}`}
              columns={activeAlertsTableColumns}
              dataSource={activeAlertsCategory === 'New' ? [...nonSnoozedActiveEvents] : [...snoozedActiveEvents]}
              pagination={false}
            />
          </ConfigProvider>
        </>
      ) : (
        renderNoActiveAlerts()
      )}
      {isCustomSnoozeModalOpen && (
        <CustomSnoozeModal
          onCancel={() => setIsCustomSnoozeModalOpen(false)}
          onSubmit={submitSnoozeAlert}
          theme={theme}
        />
      )}
    </div>
  );
};
